import React, { useState, useEffect } from "react"
import { User } from "../features/auth/types"
import HeaderSection from "../components/header-section"
import Sidebar from "../features/sidebar/components/sidebar"
import SimulationManagement from "../features/admin/components/simulation-management"
import ScoreSetsManagement from "../features/admin/components/score-sets-management"
import { useGetUserapiRolesQuery } from "../app/services/meseeknaApi"
import {
  useAdminCreateUserMutation,
  useGetAdminOrganizationsQuery,
  useGetAdminTestsQuery,
  useAssignSimulationToOrganizationMutation,
  useRemoveSimulationFromOrganizationMutation,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationSimulationsQuery,
  useGetAdminUsersQuery,
  useUpdateAdminUserMutation,
  useUpdateAdminUserPasswordMutation,
  useLazyGetUserSimulationRunsQuery,
  useSetManuallyPaidStatusMutation,
  useForgotPasswordMutation,
  type AdminCreateUserRequest,
  type AdminOrganization,
  type AdminUser,
} from "../app/services/meseeknaApiV2"
import RunLogViewer from "../features/admin/components/RunLogViewer"

// Use AdminOrganization type from API instead of local type

// Use AdminTest type from API instead of local type

type CreatedUser = User & {
  password?: string
  otpLink?: string
}

const SuperAdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "organizations" | "users" | "score-sets" | "simulations" | "all-users"
  >("organizations")
  const [createdUsers, setCreatedUsers] = useState<CreatedUser[]>([])
  const [loading, setLoading] = useState(false)

  // All Users tab pagination state
  const [allUsersPage, setAllUsersPage] = useState(1)
  const [allUsersPageSize, setAllUsersPageSize] = useState(100)

  // All Users tab sorting state
  type SortColumn = "email" | "name" | "role" | "organization" | "status" | "createdOn"
  type SortDirection = "asc" | "desc"
  const [sortColumn, setSortColumn] = useState<SortColumn>("createdOn")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

  // All Users tab search state
  const [userSearchQuery, setUserSearchQuery] = useState("")

  // User edit panel state
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [showEditPanel, setShowEditPanel] = useState(false)
  const [editUserData, setEditUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: 5,
    organizationId: "",
    status: 1,
  })
  const [newPassword, setNewPassword] = useState("")

  // Organization form state
  const [orgFormData, setOrgFormData] = useState({ organizationName: "" })
  const [editingOrg, setEditingOrg] = useState<AdminOrganization | null>(null)
  const [showOrgForm, setShowOrgForm] = useState(false)

  // Simulation assignment state
  const [showSimulationModal, setShowSimulationModal] = useState(false)
  const [selectedOrgForSimulations, setSelectedOrgForSimulations] =
    useState<AdminOrganization | null>(null)

  // Run log viewer state
  const [showRunLogViewer, setShowRunLogViewer] = useState(false)
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)

  // RTK Query hooks for existing endpoints
  const { data: rolesData } = useGetUserapiRolesQuery()
  const { data: organizationsData, isLoading: organizationsLoading, refetch: refetchOrganizations } =
    useGetAdminOrganizationsQuery()
  const { data: testsData } = useGetAdminTestsQuery()
  const { data: orgSimulationsData, refetch: refetchOrgSimulations } =
    useGetOrganizationSimulationsQuery(selectedOrgForSimulations?.id || 0, {
      skip: !selectedOrgForSimulations,
    })
  const [adminCreateUser] = useAdminCreateUserMutation()
  const [assignSimulation] = useAssignSimulationToOrganizationMutation()
  const [removeSimulation] = useRemoveSimulationFromOrganizationMutation()
  const [createOrg] = useCreateOrganizationMutation()
  const [updateOrg] = useUpdateOrganizationMutation()
  const { data: allUsersData, isLoading: allUsersLoading, refetch: refetchAllUsers } =
    useGetAdminUsersQuery()
  const [updateAdminUser, { isLoading: isUpdatingUser }] = useUpdateAdminUserMutation()
  const [fetchUserSimulationRuns, { data: userSimulationRunsData, isLoading: isLoadingSimulationRuns }] =
    useLazyGetUserSimulationRunsQuery()
  const [setManuallyPaidStatus, { isLoading: isSettingManuallyPaid }] = useSetManuallyPaidStatusMutation()
  const [updateAdminUserPassword, { isLoading: isUpdatingPassword }] = useUpdateAdminUserPasswordMutation()
  const [forgotPassword, { isLoading: isSendingResetEmail }] = useForgotPasswordMutation()

  const organizations = organizationsData?.data || []
  const availableSimulations = testsData?.data || []
  const orgSimulations = orgSimulationsData?.data || []
  const roles = rolesData?.data || []
  const allUsers = allUsersData?.data || []

  // Sort handler for All Users table
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortColumn(column)
      setSortDirection("asc")
    }
    setAllUsersPage(1) // Reset to first page when sorting
  }

  // Filter users by search query
  const filteredUsers = userSearchQuery.trim()
    ? allUsers.filter((user) => {
        const query = userSearchQuery.toLowerCase()
        const email = user.email.toLowerCase()
        const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim().toLowerCase()
        const orgName = user.organization?.organizationName?.toLowerCase() || ""
        return email.includes(query) || fullName.includes(query) || orgName.includes(query)
      })
    : allUsers

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aValue: string | number
    let bValue: string | number

    switch (sortColumn) {
      case "email":
        aValue = a.email.toLowerCase()
        bValue = b.email.toLowerCase()
        break
      case "name":
        aValue = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase()
        bValue = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase()
        break
      case "role":
        aValue = a.roles?.[0]?.name?.toLowerCase() || ""
        bValue = b.roles?.[0]?.name?.toLowerCase() || ""
        break
      case "organization":
        aValue = a.organization?.organizationName?.toLowerCase() || ""
        bValue = b.organization?.organizationName?.toLowerCase() || ""
        break
      case "status":
        aValue = a.status
        bValue = b.status
        break
      case "createdOn":
        aValue = new Date(a.createdOn).getTime()
        bValue = new Date(b.createdOn).getTime()
        break
      default:
        return 0
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
    return 0
  })

  // All Users pagination calculations
  const totalAllUsersPages = Math.ceil(sortedUsers.length / allUsersPageSize)
  const paginatedUsers = sortedUsers.slice(
    (allUsersPage - 1) * allUsersPageSize,
    allUsersPage * allUsersPageSize,
  )

  // User form state
  const [userFormData, setUserFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    role: 5, // Default to SimulationUser
    organizationId: "",
    passwordOption: "otp-set", // 'auto', 'otp-set', 'otp-signin'
  })

  // Batch user state
  const [batchEmails, setBatchEmails] = useState("")
  const [batchRole, setBatchRole] = useState(5)
  const [batchOrgId, setBatchOrgId] = useState("")
  const [batchPasswordOption, setBatchPasswordOption] = useState("otp-set")

  // Load data from localStorage and mock endpoints on mount
  useEffect(() => {
    const stored = localStorage.getItem("admin-created-users")
    if (stored) {
      const { data, timestamp } = JSON.parse(stored)
      // Check if data is less than 24 hours old
      if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
        setCreatedUsers(data)
      } else {
        localStorage.removeItem("admin-created-users")
      }
    }

    // Data is now loaded via RTK Query hooks
  }, [])

  // Save created users to localStorage
  const saveUsersToStorage = (users: CreatedUser[]) => {
    const dataToStore = {
      data: users,
      timestamp: Date.now(),
    }
    localStorage.setItem("admin-created-users", JSON.stringify(dataToStore))
    setCreatedUsers(users)
  }

  // Organization simulations are now fetched via RTK Query hook

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      await createOrg({
        organizationName: orgFormData.organizationName,
      }).unwrap()
      setShowOrgForm(false)
      setOrgFormData({ organizationName: "" })
    } catch (error) {
      console.error("Error creating organization:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleEditOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingOrg) return

    setLoading(true)
    try {
      await updateOrg({
        id: editingOrg.id,
        data: { organizationName: orgFormData.organizationName },
      }).unwrap()
      setEditingOrg(null)
      setShowOrgForm(false)
      setOrgFormData({ organizationName: "" })
    } catch (error) {
      console.error("Error updating organization:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleAssignSimulation = async (simulationId: number) => {
    if (!selectedOrgForSimulations) return

    try {
      await assignSimulation({
        orgId: selectedOrgForSimulations.id,
        testId: simulationId,
        body: { userId: 0 }, // For organization-level assignment
      }).unwrap()

      // Refresh org simulations
      await refetchOrgSimulations()
    } catch (error) {
      console.error("Error assigning simulation:", error)
    }
  }

  const handleRemoveSimulation = async (simulationId: number) => {
    if (!selectedOrgForSimulations) return

    try {
      await removeSimulation({
        orgId: selectedOrgForSimulations.id,
        testId: simulationId,
      }).unwrap()

      // Refresh org simulations
      await refetchOrgSimulations()
    } catch (error) {
      console.error("Error removing simulation:", error)
    }
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Prepare request based on password option
      const requestData: AdminCreateUserRequest = {
        email: userFormData.email,
        organizationId: parseInt(userFormData.organizationId),
        firstName: userFormData.firstName,
        lastName: userFormData.lastName,
        role: userFormData.role,
      }

      // Set password options based on selection
      if (userFormData.passwordOption === "auto") {
        // Auto-generate password (no email sent)
        requestData.password =
          Math.random().toString(36).slice(-8) +
          Math.random().toString(36).slice(-4).toUpperCase()
        requestData.sendEmail = false
      } else if (userFormData.passwordOption === "otp-set") {
        // Send OTP link for password setting
        requestData.sendEmail = true
        requestData.linkType = "onboarding"
      } else if (userFormData.passwordOption === "otp-signin") {
        // Send OTP link for immediate login
        requestData.sendEmail = true
        requestData.linkType = "login"
      }

      const result = await adminCreateUser(requestData).unwrap()

      if (result.status === "success" && result.data) {
        const newUser: CreatedUser = {
          id: result.data.id,
          firstName: userFormData.firstName,
          lastName: userFormData.lastName,
          email: result.data.email,
          organizationId: result.data.organizationId,
          role: userFormData.role,
          userHash: "",
          token: "",
          imageUrl: "",
          createdOn: new Date().toISOString(),
          userName: result.data.email,
          status: 1,
          expire_status: 1,
          roleName:
            roles.find((r) => r.id === userFormData.role)?.roleName ||
            "Unknown",
          organizationName:
            organizations.find(
              (o) => o.id === parseInt(userFormData.organizationId),
            )?.organizationName || "Unknown",
          roles: result.data.roles || [],
          password:
            userFormData.passwordOption === "auto"
              ? requestData.password
              : undefined,
          otpLink:
            userFormData.passwordOption !== "auto"
              ? result.data.otpLink
              : undefined,
        }
        const updatedUsers = [...createdUsers, newUser]
        saveUsersToStorage(updatedUsers)

        // Reset form
        setUserFormData({
          email: "",
          firstName: "",
          lastName: "",
          role: 5,
          organizationId: "",
          passwordOption: "otp-set",
        })
      }
    } catch (error) {
      console.error("Error creating user:", error)
      alert("Error creating user. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleBatchCreateUsers = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    const emails = batchEmails.split("\n").filter((email) => email.trim())

    try {
      const newUsers = []
      let successCount = 0
      let errorCount = 0

      for (const email of emails) {
        try {
          // Prepare request based on password option
          const requestData: AdminCreateUserRequest = {
            email: email.trim(),
            organizationId: parseInt(batchOrgId),
            firstName: "",
            lastName: "",
            role: batchRole,
          }

          // Set password options based on selection
          if (batchPasswordOption === "auto") {
            // Auto-generate password (no email sent)
            requestData.password =
              Math.random().toString(36).slice(-8) +
              Math.random().toString(36).slice(-4).toUpperCase()
            requestData.sendEmail = false
          } else if (batchPasswordOption === "otp-set") {
            // Send OTP link for password setting
            requestData.sendEmail = true
            requestData.linkType = "onboarding"
          } else if (batchPasswordOption === "otp-signin") {
            // Send OTP link for immediate login
            requestData.sendEmail = true
            requestData.linkType = "login"
          }

          const result = await adminCreateUser(requestData).unwrap()

          if (result.status === "success" && result.data) {
            const newUser: CreatedUser = {
              id: result.data.id,
              firstName: "",
              lastName: "",
              email: result.data.email,
              organizationId: result.data.organizationId,
              role: batchRole,
              userHash: "",
              token: "",
              imageUrl: "",
              createdOn: new Date().toISOString(),
              userName: result.data.email,
              status: 1,
              expire_status: 1,
              roleName:
                roles.find((r) => r.id === batchRole)?.roleName || "Unknown",
              organizationName:
                organizations.find((o) => o.id === parseInt(batchOrgId))
                  ?.organizationName || "Unknown",
              roles: result.data.roles || [],
              password:
                batchPasswordOption === "auto"
                  ? requestData.password
                  : undefined,
              otpLink:
                batchPasswordOption !== "auto"
                  ? result.data.otpLink
                  : undefined,
            }
            newUsers.push(newUser)
            successCount++
          }
        } catch (userError) {
          console.error(`Error creating user ${email}:`, userError)
          errorCount++
        }
      }

      if (newUsers.length > 0) {
        const updatedUsers = [...createdUsers, ...newUsers]
        saveUsersToStorage(updatedUsers)

        // Reset form
        setBatchEmails("")
        setBatchRole(5)
        setBatchOrgId("")
        setBatchPasswordOption("otp-set")
      }

      // Show summary
      if (errorCount > 0) {
        alert(
          `Batch creation completed. ${successCount} users created successfully, ${errorCount} failed.`,
        )
      } else {
        alert(`All ${successCount} users created successfully!`)
      }
    } catch (error) {
      console.error("Error creating batch users:", error)
      alert("Error creating batch users. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const exportOrganizationsToCSV = () => {
    const headers = [
      "Organization Name",
      "User Count",
      "Onboarding Status",
      "Onboarding Completed At",
      "Created Date",
    ]
    const csvData = [headers]

    organizations.forEach((org) => {
      const onboardingStatus = org.onboardingCompleted
        ? "Completed"
        : org.onboardingAppointmentScheduled
          ? "Scheduled"
          : "Pending"

      const completedAt = org.onboardingCompletedAt
        ? new Date(org.onboardingCompletedAt).toLocaleDateString()
        : ""

      csvData.push([
        org.organizationName,
        org._count.users.toString(),
        onboardingStatus,
        completedAt,
        new Date(org.createdOn).toLocaleDateString(),
      ])
    })

    const csvString = csvData
      .map((row) =>
        row.map((field) => `"${field.replace(/"/g, '""')}"`).join(","),
      )
      .join("\n")

    copyToClipboard(csvString)

    // Show feedback
    const button = document.getElementById("csv-export-button")
    if (button) {
      const originalText = button.textContent
      button.textContent = "Copied!"
      setTimeout(() => {
        button.textContent = originalText
      }, 2000)
    }
  }

  const exportUsersToCSV = () => {
    const headers = [
      "Email",
      "First Name",
      "Last Name",
      "Role",
      "Organization",
      "Password/Link",
      "Created Date",
    ]
    const csvData = [headers]

    createdUsers.forEach((user) => {
      const passwordOrLink = user.password
        ? user.password
        : user.otpLink || "N/A"

      csvData.push([
        user.email,
        user.firstName || "",
        user.lastName || "",
        user.roleName,
        user.organizationName,
        passwordOrLink,
        new Date(user.createdOn).toLocaleDateString(),
      ])
    })

    const csvString = csvData
      .map((row) =>
        row.map((field) => `"${field.replace(/"/g, '""')}"`).join(","),
      )
      .join("\n")

    copyToClipboard(csvString)

    // Show feedback
    const button = document.getElementById("csv-export-users-button")
    if (button) {
      const originalText = button.textContent
      button.textContent = "Copied!"
      setTimeout(() => {
        button.textContent = originalText
      }, 2000)
    }
  }

  const openSimulationModal = (org: AdminOrganization) => {
    setSelectedOrgForSimulations(org)
    setShowSimulationModal(true)
    // RTK Query will automatically fetch simulations when selectedOrgForSimulations changes
  }

  const handleManuallyPaidToggle = async (orgId: number, currentStatus: boolean) => {
    try {
      await setManuallyPaidStatus({
        organizationId: orgId,
        isManuallyPaid: !currentStatus,
      }).unwrap()
      refetchOrganizations()
    } catch (error) {
      console.error("Failed to update manually paid status:", error)
      // RTK Query errors can have different structures
      const rtqError = error as { data?: { message?: string }; status?: number; error?: string }
      const errorMessage = rtqError?.data?.message ||
        rtqError?.error ||
        (error as Error)?.message ||
        JSON.stringify(error)
      alert(`Failed to update manual paid status (status: ${rtqError?.status || 'unknown'}): ${errorMessage}`)
    }
  }

  const openEditUserPanel = (user: AdminUser) => {
    setSelectedUser(user)
    setEditUserData({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email,
      role: user.roles?.[0]?.roleId || 5,
      organizationId: user.organizationId?.toString() || "",
      status: user.status,
    })
    setNewPassword("")
    setShowEditPanel(true)
    // Fetch user's simulation runs for run log viewing
    fetchUserSimulationRuns(user.id)
  }

  const openRunLogViewer = (runId: number) => {
    setSelectedRunId(runId)
    setShowRunLogViewer(true)
  }

  const closeRunLogViewer = () => {
    setShowRunLogViewer(false)
    setSelectedRunId(null)
  }

  const closeEditUserPanel = () => {
    setShowEditPanel(false)
    setSelectedUser(null)
    setNewPassword("")
  }

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUser) return

    try {
      await updateAdminUser({
        id: selectedUser.id,
        firstName: editUserData.firstName,
        lastName: editUserData.lastName,
        email: editUserData.email,
        role: editUserData.role,
        organizationId: parseInt(editUserData.organizationId),
        status: editUserData.status,
      }).unwrap()

      await refetchAllUsers()
      closeEditUserPanel()
    } catch (error) {
      console.error("Error updating user:", error)
      alert("Error updating user. Please try again.")
    }
  }

  const handleSetPassword = async () => {
    if (!selectedUser || !newPassword.trim()) return

    try {
      await updateAdminUserPassword({
        userId: selectedUser.id,
        newPassword: newPassword.trim(),
      }).unwrap()
      alert("Password updated successfully")
      setNewPassword("")
    } catch (error) {
      console.error("Error setting password:", error)
      alert("Error setting password. Please try again.")
    }
  }

  const handleSendResetEmail = async () => {
    if (!selectedUser) return

    try {
      await forgotPassword({ email: selectedUser.email }).unwrap()
      alert(`Password reset email sent to ${selectedUser.email}`)
    } catch (error) {
      console.error("Error sending reset email:", error)
      alert("Error sending password reset email. Please try again.")
    }
  }

  const generateRandomPassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*"
    let password = ""
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setNewPassword(password)
  }

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <HeaderSection />
        <div className="flex-1 overflow-auto bg-gray-50">
          {/* Page Header */}
          <div className="bg-white shadow">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-6">
                <h1 className="text-3xl font-bold text-gray-900">
                  Super Admin Dashboard
                </h1>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("organizations")}
                  className={`py-3 px-2 border-b-2 font-medium text-sm cursor-pointer transition-colors ${
                    activeTab === "organizations"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}>
                  Organizations
                </button>
                <button
                  onClick={() => setActiveTab("users")}
                  className={`py-3 px-2 border-b-2 font-medium text-sm cursor-pointer transition-colors ${
                    activeTab === "users"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}>
                  Create Users
                </button>
                <button
                  onClick={() => setActiveTab("all-users")}
                  className={`py-3 px-2 border-b-2 font-medium text-sm cursor-pointer transition-colors ${
                    activeTab === "all-users"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}>
                  All Users
                </button>
                <button
                  onClick={() => setActiveTab("simulations")}
                  className={`py-3 px-2 border-b-2 font-medium text-sm cursor-pointer transition-colors ${
                    activeTab === "simulations"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}>
                  Simulations
                </button>
                <button
                  onClick={() => setActiveTab("score-sets")}
                  className={`py-3 px-2 border-b-2 font-medium text-sm cursor-pointer transition-colors ${
                    activeTab === "score-sets"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}>
                  Score Sets
                </button>
              </nav>
            </div>

            {/* Organizations Tab */}
            {activeTab === "organizations" && (
              <div className="py-6">
                <div className="mb-6 flex justify-between items-center">
                  <button
                    onClick={() => {
                      setShowOrgForm(true)
                      setEditingOrg(null)
                      setOrgFormData({ organizationName: "" })
                    }}
                    className="bg-indigo-600 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer transition-colors">
                    Create Organization
                  </button>
                  <button
                    id="csv-export-button"
                    onClick={exportOrganizationsToCSV}
                    disabled={
                      organizationsLoading || organizations.length === 0
                    }
                    className="bg-green-600 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors">
                    Export CSV
                  </button>
                </div>

                {/* Organization Form Modal */}
                {showOrgForm && (
                  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                      <h3 className="text-lg font-medium mb-4">
                        {editingOrg
                          ? "Edit Organization"
                          : "Create Organization"}
                      </h3>
                      <form
                        onSubmit={editingOrg ? handleEditOrg : handleCreateOrg}>
                        <div className="mb-4">
                          <label
                            htmlFor="org-name"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Organization Name{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="org-name"
                            type="text"
                            value={orgFormData.organizationName}
                            onChange={(e) =>
                              setOrgFormData({
                                organizationName: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                            placeholder="Enter organization name"
                            required
                          />
                        </div>
                        <div className="flex justify-end space-x-3">
                          <button
                            type="button"
                            onClick={() => setShowOrgForm(false)}
                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md text-sm font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 cursor-pointer transition-colors">
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors">
                            {loading
                              ? "Saving..."
                              : editingOrg
                                ? "Update"
                                : "Create"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Simulation Assignment Modal */}
                {showSimulationModal && selectedOrgForSimulations && (
                  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                      <h3 className="text-lg font-medium mb-4">
                        Manage Simulations for{" "}
                        {selectedOrgForSimulations.organizationName}
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Assigned Simulations */}
                        <div>
                          <h4 className="font-medium text-gray-900 mb-3">
                            Assigned Simulations
                          </h4>
                          <div className="space-y-2">
                            {orgSimulations.map((simulation) => (
                              <div
                                key={simulation.id}
                                className="flex items-center justify-between p-3 bg-green-50 rounded-md">
                                <div>
                                  <div className="font-medium">
                                    {simulation.simulation?.title || "No title"}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {simulation.simulation?.shortName ||
                                      "No description"}
                                  </div>
                                </div>
                                <button
                                  onClick={() =>
                                    handleRemoveSimulation(simulation.testId)
                                  }
                                  className="text-red-600 hover:text-red-800 text-sm font-medium cursor-pointer transition-colors">
                                  Remove
                                </button>
                              </div>
                            ))}
                            {orgSimulations.length === 0 && (
                              <div className="text-gray-500 text-sm">
                                No simulations assigned
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Available Simulations */}
                        <div>
                          <h4 className="font-medium text-gray-900 mb-3">
                            Available Simulations
                          </h4>
                          <div className="space-y-2">
                            {availableSimulations
                              .filter(
                                (simulation) =>
                                  !orgSimulations.some(
                                    (orgSim) => orgSim.testId === simulation.id,
                                  ),
                              )
                              .map((simulation) => (
                                <div
                                  key={simulation.id}
                                  className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                                  <div>
                                    <div className="font-medium">
                                      {simulation.title}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {simulation.shortName}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleAssignSimulation(simulation.id)
                                    }
                                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium cursor-pointer transition-colors">
                                    Assign
                                  </button>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end mt-6">
                        <button
                          onClick={() => setShowSimulationModal(false)}
                          className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 cursor-pointer transition-colors">
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Organizations Table */}
                {organizationsLoading ? (
                  <div className="bg-white shadow sm:rounded-md p-6">
                    <div className="flex items-center justify-center py-8">
                      <div className="text-gray-500">
                        Loading organizations...
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Organization
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Users
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Onboarding
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Manual Paid
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {organizations.map((org) => (
                          <tr key={org.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {org.organizationName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {org._count.users} users
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex flex-col space-y-1">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                    org.onboardingCompleted
                                      ? "bg-green-100 text-green-800"
                                      : org.onboardingAppointmentScheduled
                                        ? "bg-yellow-100 text-yellow-800"
                                        : "bg-red-100 text-red-800"
                                  }`}>
                                  {org.onboardingCompleted
                                    ? "Completed"
                                    : org.onboardingAppointmentScheduled
                                      ? "Scheduled"
                                      : "Pending"}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(org.createdOn).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={org.isManuallyPaid ?? false}
                                  onChange={() => handleManuallyPaidToggle(org.id, org.isManuallyPaid ?? false)}
                                  disabled={isSettingManuallyPaid}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                              </label>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => {
                                  setEditingOrg(org)
                                  setOrgFormData({
                                    organizationName: org.organizationName,
                                  })
                                  setShowOrgForm(true)
                                }}
                                className="text-indigo-600 hover:text-indigo-900 text-sm font-medium mr-4 cursor-pointer transition-colors">
                                Edit
                              </button>
                              <button
                                onClick={() => openSimulationModal(org)}
                                className="text-green-600 hover:text-green-900 text-sm font-medium cursor-pointer transition-colors">
                                Manage Simulations
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === "users" && (
              <div className="py-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {/* Single User Creation */}
                  <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 h-fit">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">
                      Create Single User
                    </h3>
                    <form
                      onSubmit={handleCreateUser}
                      className="flex flex-col h-full">
                      <div className="space-y-5 flex-1">
                        <div className="flex-1 flex flex-col">
                          <label
                            htmlFor="single-email"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="single-email"
                            type="email"
                            value={userFormData.email}
                            onChange={(e) =>
                              setUserFormData({
                                ...userFormData,
                                email: e.target.value,
                              })
                            }
                            className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                            placeholder="user@company.com"
                            required
                          />
                        </div>

                        <div className="gap-4 flex-1 flex flex-row">
                          <div className="flex-1 flex flex-col">
                            <label
                              htmlFor="single-firstname"
                              className="block text-sm font-medium text-gray-700 mb-2">
                              First Name
                            </label>
                            <input
                              id="single-firstname"
                              type="text"
                              value={userFormData.firstName}
                              onChange={(e) =>
                                setUserFormData({
                                  ...userFormData,
                                  firstName: e.target.value,
                                })
                              }
                              className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                              placeholder="John"
                            />
                          </div>
                          <div className="flex-1 flex flex-col">
                            <label
                              htmlFor="single-lastname"
                              className="block text-sm font-medium text-gray-700 mb-2">
                              Last Name
                            </label>
                            <input
                              id="single-lastname"
                              type="text"
                              value={userFormData.lastName}
                              onChange={(e) =>
                                setUserFormData({
                                  ...userFormData,
                                  lastName: e.target.value,
                                })
                              }
                              className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                              placeholder="Doe"
                            />
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="single-organization"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Organization <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              id="single-organization"
                              value={userFormData.organizationId}
                              onChange={(e) =>
                                setUserFormData({
                                  ...userFormData,
                                  organizationId: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors appearance-none cursor-pointer"
                              required>
                              <option value="">Select Organization</option>
                              {organizations.map((org) => (
                                <option key={org.id} value={org.id}>
                                  {org.organizationName}
                                </option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <svg
                                className="h-4 w-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="single-role"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Role
                          </label>
                          <div className="relative">
                            <select
                              id="single-role"
                              value={userFormData.role}
                              onChange={(e) =>
                                setUserFormData({
                                  ...userFormData,
                                  role: parseInt(e.target.value),
                                })
                              }
                              className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors appearance-none cursor-pointer">
                              {roles.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {role.roleName}
                                </option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <svg
                                className="h-4 w-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="single-password-option"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Password Option
                          </label>
                          <div className="relative">
                            <select
                              id="single-password-option"
                              value={userFormData.passwordOption}
                              onChange={(e) =>
                                setUserFormData({
                                  ...userFormData,
                                  passwordOption: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors appearance-none cursor-pointer">
                              <option value="auto">
                                Generate password (display in table)
                              </option>
                              <option value="otp-set">
                                Send onboarding link to set password
                              </option>
                              <option value="otp-signin">
                                Send onboarding link for immediate sign-in
                              </option>
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <svg
                                className="h-4 w-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-indigo-600 text-white py-2.5 px-4 rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors">
                          {loading ? "Creating..." : "Create User"}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Batch User Creation */}
                  <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 h-fit">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">
                      Batch Create Users
                    </h3>
                    <form
                      onSubmit={handleBatchCreateUsers}
                      className="flex flex-col h-full">
                      <div className="space-y-5 flex-1">
                        <div>
                          <label
                            htmlFor="batch-emails"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Email Addresses (one per line){" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="batch-emails"
                            value={batchEmails}
                            onChange={(e) => setBatchEmails(e.target.value)}
                            rows={10}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors resize-none"
                            placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                            required
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="batch-organization"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Organization <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              id="batch-organization"
                              value={batchOrgId}
                              onChange={(e) => setBatchOrgId(e.target.value)}
                              className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors appearance-none cursor-pointer"
                              required>
                              <option value="">Select Organization</option>
                              {organizations.map((org) => (
                                <option key={org.id} value={org.id}>
                                  {org.organizationName}
                                </option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <svg
                                className="h-4 w-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="batch-role"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Role
                          </label>
                          <div className="relative">
                            <select
                              id="batch-role"
                              value={batchRole}
                              onChange={(e) =>
                                setBatchRole(parseInt(e.target.value))
                              }
                              className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors appearance-none cursor-pointer">
                              {roles.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {role.roleName}
                                </option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <svg
                                className="h-4 w-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="batch-password-option"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            Password Option
                          </label>
                          <div className="relative">
                            <select
                              id="batch-password-option"
                              value={batchPasswordOption}
                              onChange={(e) =>
                                setBatchPasswordOption(e.target.value)
                              }
                              className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors appearance-none cursor-pointer">
                              <option value="auto">
                                Generate password (display in table)
                              </option>
                              <option value="otp-set">
                                Send onboarding link to set password
                              </option>
                              <option value="otp-signin">
                                Send onboarding link for immediate sign-in
                              </option>
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <svg
                                className="h-4 w-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-green-600 text-white py-2.5 px-4 rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors">
                          {loading ? "Creating..." : "Create Batch Users"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Created Users Table */}
                {createdUsers.length > 0 && (
                  <div className="mt-8 bg-white shadow rounded-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium">
                        Created Users (Last 24 Hours)
                      </h3>
                      <button
                        id="csv-export-users-button"
                        onClick={exportUsersToCSV}
                        className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 cursor-pointer transition-colors">
                        Export CSV
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Email
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Name
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Role
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Organization
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Password/Link
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Created
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {createdUsers.map((user) => (
                            <tr key={user.id} className="text-sm">
                              <td className="px-4 py-2">{user.email}</td>
                              <td className="px-4 py-2">
                                {`${user.firstName} ${user.lastName}`.trim() ||
                                  "-"}
                              </td>
                              <td className="px-4 py-2">{user.roleName}</td>
                              <td className="px-4 py-2">
                                {user.organizationName}
                              </td>
                              <td className="px-4 py-2">
                                {user.password && (
                                  <button
                                    className="text-blue-600 hover:text-blue-800 font-mono text-sm font-medium cursor-pointer transition-colors"
                                    onClick={() =>
                                      copyToClipboard(user.password!)
                                    }
                                    title="Click to copy password">
                                    {user.password}
                                  </button>
                                )}
                                {user.otpLink && (
                                  <button
                                    className="text-blue-600 hover:text-blue-800 text-xs font-medium cursor-pointer transition-colors"
                                    onClick={() =>
                                      copyToClipboard(user.otpLink!)
                                    }
                                    title="Click to copy OTP link">
                                    OTP Link
                                  </button>
                                )}
                              </td>
                              <td className="px-4 py-2">
                                {new Date(user.createdOn).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                      Click on passwords or OTP links to copy to clipboard
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Score Sets Tab */}
            {activeTab === "score-sets" && <ScoreSetsManagement />}

            {/* Simulations Tab */}
            {activeTab === "simulations" && <SimulationManagement />}

            {/* All Users Tab */}
            {activeTab === "all-users" && (
              <div className="py-6">
                <div className="mb-6 flex flex-col gap-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        All Users
                      </h2>
                      <p className="text-sm text-gray-500">
                        {userSearchQuery.trim()
                          ? `${filteredUsers.length} of ${allUsers.length} users`
                          : `${allUsers.length} total users`}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <label
                        htmlFor="page-size"
                        className="text-sm text-gray-600">
                        Show:
                      </label>
                      <select
                        id="page-size"
                        value={allUsersPageSize}
                        onChange={(e) => {
                          setAllUsersPageSize(Number(e.target.value))
                          setAllUsersPage(1)
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors cursor-pointer">
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                        <option value={200}>200 per page</option>
                      </select>
                    </div>
                  </div>
                  {/* Search input */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by email, name, or organization..."
                      value={userSearchQuery}
                      onChange={(e) => {
                        setUserSearchQuery(e.target.value)
                        setAllUsersPage(1)
                      }}
                      className="w-full md:w-96 px-4 py-2 pl-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <svg
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {userSearchQuery && (
                      <button
                        onClick={() => {
                          setUserSearchQuery("")
                          setAllUsersPage(1)
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {allUsersLoading ? (
                  <div className="bg-white shadow sm:rounded-md p-6">
                    <div className="flex items-center justify-center py-8">
                      <div className="text-gray-500">Loading users...</div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              onClick={() => handleSort("email")}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none">
                              <div className="flex items-center space-x-1">
                                <span>Email</span>
                                {sortColumn === "email" && (
                                  <span className="text-indigo-600">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort("name")}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none">
                              <div className="flex items-center space-x-1">
                                <span>Name</span>
                                {sortColumn === "name" && (
                                  <span className="text-indigo-600">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort("role")}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none">
                              <div className="flex items-center space-x-1">
                                <span>Role</span>
                                {sortColumn === "role" && (
                                  <span className="text-indigo-600">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort("organization")}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none">
                              <div className="flex items-center space-x-1">
                                <span>Organization</span>
                                {sortColumn === "organization" && (
                                  <span className="text-indigo-600">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort("status")}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none">
                              <div className="flex items-center space-x-1">
                                <span>Status</span>
                                {sortColumn === "status" && (
                                  <span className="text-indigo-600">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort("createdOn")}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none">
                              <div className="flex items-center space-x-1">
                                <span>Created</span>
                                {sortColumn === "createdOn" && (
                                  <span className="text-indigo-600">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedUsers.map((user) => (
                            <tr
                              key={user.id}
                              onClick={() => openEditUserPanel(user)}
                              className="cursor-pointer hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {user.email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                                  "-"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {user.roles?.[0]?.name || "Unknown"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {user.organization?.organizationName || "-"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                    user.status === 1
                                      ? "bg-green-100 text-green-800"
                                      : user.status === 0
                                        ? "bg-red-100 text-red-800"
                                        : "bg-yellow-100 text-yellow-800"
                                  }`}>
                                  {user.status === 1
                                    ? "Active"
                                    : user.status === 0
                                      ? "Inactive"
                                      : "Pending"}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(user.createdOn).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination controls */}
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        Showing{" "}
                        {filteredUsers.length > 0
                          ? (allUsersPage - 1) * allUsersPageSize + 1
                          : 0}{" "}
                        to{" "}
                        {Math.min(
                          allUsersPage * allUsersPageSize,
                          filteredUsers.length,
                        )}{" "}
                        of {filteredUsers.length} users
                        {userSearchQuery.trim() && ` (filtered from ${allUsers.length})`}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() =>
                            setAllUsersPage((p) => Math.max(1, p - 1))
                          }
                          disabled={allUsersPage === 1}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors">
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {allUsersPage} of{" "}
                          {totalAllUsersPages || 1}
                        </span>
                        <button
                          onClick={() =>
                            setAllUsersPage((p) =>
                              Math.min(totalAllUsersPages, p + 1),
                            )
                          }
                          disabled={allUsersPage >= totalAllUsersPages}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors">
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit User Slide-in Panel */}
      <div
        className={`fixed inset-0 overflow-hidden z-50 ${showEditPanel ? "" : "pointer-events-none"}`}>
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-gray-500 transition-opacity duration-300 ${showEditPanel ? "bg-opacity-75" : "bg-opacity-0"}`}
          onClick={closeEditUserPanel}
        />

        {/* Slide-in Panel */}
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-md transform transition-transform duration-300 ease-in-out ${showEditPanel ? "translate-x-0" : "translate-x-full"}`}>
            <div className="h-full flex flex-col bg-white shadow-xl">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">
                  Edit User
                </h2>
                <button
                  onClick={closeEditUserPanel}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Form */}
              <div className="flex-1 overflow-y-auto p-6">
                {selectedUser && (
                  <form onSubmit={handleUpdateUser} className="space-y-5">
                    <div>
                      <label
                        htmlFor="edit-email"
                        className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        id="edit-email"
                        type="email"
                        value={editUserData.email}
                        onChange={(e) =>
                          setEditUserData({
                            ...editUserData,
                            email: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="edit-firstname"
                          className="block text-sm font-medium text-gray-700 mb-2">
                          First Name
                        </label>
                        <input
                          id="edit-firstname"
                          type="text"
                          value={editUserData.firstName}
                          onChange={(e) =>
                            setEditUserData({
                              ...editUserData,
                              firstName: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="edit-lastname"
                          className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name
                        </label>
                        <input
                          id="edit-lastname"
                          type="text"
                          value={editUserData.lastName}
                          onChange={(e) =>
                            setEditUserData({
                              ...editUserData,
                              lastName: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="edit-organization"
                        className="block text-sm font-medium text-gray-700 mb-2">
                        Organization
                      </label>
                      <div className="relative">
                        <select
                          id="edit-organization"
                          value={editUserData.organizationId}
                          onChange={(e) =>
                            setEditUserData({
                              ...editUserData,
                              organizationId: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors appearance-none cursor-pointer">
                          <option value="">No Organization</option>
                          {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.organizationName} (ID: {org.id})
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <svg
                            className="h-4 w-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="edit-role"
                        className="block text-sm font-medium text-gray-700 mb-2">
                        Role
                      </label>
                      <div className="relative">
                        <select
                          id="edit-role"
                          value={editUserData.role}
                          onChange={(e) =>
                            setEditUserData({
                              ...editUserData,
                              role: parseInt(e.target.value),
                            })
                          }
                          className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors appearance-none cursor-pointer">
                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.roleName}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <svg
                            className="h-4 w-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <label
                        htmlFor="edit-status"
                        className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <div className="relative">
                        <select
                          id="edit-status"
                          value={editUserData.status}
                          onChange={(e) =>
                            setEditUserData({
                              ...editUserData,
                              status: parseInt(e.target.value),
                            })
                          }
                          className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors appearance-none cursor-pointer">
                          <option value={1}>Active</option>
                          <option value={0}>Inactive</option>
                          <option value={2}>Pending</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <svg
                            className="h-4 w-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="pt-4 border-t border-gray-200">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">
                        User Information
                      </h3>
                      <div className="space-y-2 text-sm text-gray-500">
                        <div className="flex justify-between">
                          <span>User ID:</span>
                          <span className="font-mono">{selectedUser.id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Created:</span>
                          <span>
                            {new Date(
                              selectedUser.createdOn,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Password Management */}
                    <div className="pt-4 border-t border-gray-200">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">
                        Password Management
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <label
                            htmlFor="new-password"
                            className="block text-sm font-medium text-gray-600 mb-1.5">
                            Set New Password
                          </label>
                          <div className="flex gap-2">
                            <input
                              id="new-password"
                              type="text"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Enter new password"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                            />
                            <button
                              type="button"
                              onClick={generateRandomPassword}
                              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 cursor-pointer transition-colors whitespace-nowrap"
                              title="Generate random password">
                              Generate
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={handleSetPassword}
                            disabled={isUpdatingPassword || !newPassword.trim()}
                            className="mt-2 w-full px-3 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors">
                            {isUpdatingPassword ? "Setting Password..." : "Set Password"}
                          </button>
                        </div>
                        <div className="border-t border-gray-100 pt-3">
                          <button
                            type="button"
                            onClick={handleSendResetEmail}
                            disabled={isSendingResetEmail}
                            className="w-full px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors">
                            {isSendingResetEmail ? "Sending..." : "Send Password Reset Email"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Simulation Runs with Run Logs */}
                    <div className="pt-4 border-t border-gray-200">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">
                        Simulation Runs
                      </h3>
                      {isLoadingSimulationRuns ? (
                        <div className="flex items-center justify-center py-4">
                          <span className="loading loading-spinner loading-sm mr-2"></span>
                          <span className="text-sm text-gray-500">Loading simulation runs...</span>
                        </div>
                      ) : userSimulationRunsData?.data?.runs?.length ? (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {userSimulationRunsData.data.runs.map((run) => (
                            <div
                              key={run.runId}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded-md text-sm"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-700 truncate">
                                  {run.simulationName}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {run.dateUserEnd
                                    ? `Completed ${new Date(run.dateUserEnd).toLocaleDateString()}`
                                    : run.dateUserStart
                                      ? `Started ${new Date(run.dateUserStart).toLocaleDateString()}`
                                      : `Assigned ${new Date(run.dateAssigned).toLocaleDateString()}`}
                                </div>
                              </div>
                              {run.hasRunLogs ? (
                                <button
                                  type="button"
                                  onClick={() => openRunLogViewer(run.runId)}
                                  className="ml-2 px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded transition-colors"
                                >
                                  View Logs ({run.runLogCount})
                                </button>
                              ) : (
                                <span className="ml-2 text-xs text-gray-400">
                                  No logs
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          No simulation runs found for this user.
                        </p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-4 border-t border-gray-200 flex space-x-3">
                      <button
                        type="button"
                        onClick={closeEditUserPanel}
                        className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-200 rounded-md text-sm font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 cursor-pointer transition-colors">
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isUpdatingUser}
                        className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors">
                        {isUpdatingUser ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Run Log Viewer Modal */}
      {showRunLogViewer && selectedRunId && (
        <RunLogViewer runId={selectedRunId} onClose={closeRunLogViewer} />
      )}
    </div>
  )
}

export default SuperAdminDashboard
